import React, { useEffect, useState, useRef } from 'react';
import { Heart, MessageCircle, Share2, User, Eye } from 'lucide-react';
import { getAvatarForShadow } from '../../utils/shadowIdentity';

export const NativeAdCard = () => {
  const [adFilled, setAdFilled] = useState(false);
  const [randomStats, setRandomStats] = useState({ likes: 0, comments: 0, views: 0, shares: 0 });
  const adRef = useRef(null);

  useEffect(() => {
    // Generate random stats on mount so they stay consistent for the ad instance
    setRandomStats({
      likes: Math.floor(Math.random() * 50) + 10,
      comments: Math.floor(Math.random() * 20) + 1,
      views: Math.floor(Math.random() * 500) + 100,
      shares: Math.floor(Math.random() * 5)
    });

    try {
      if (window.adsbygoogle) {
        window.adsbygoogle.push({});
      }
    } catch (e) {
      console.error("AdSense push error:", e);
    }
  }, []);

  useEffect(() => {
    // Watch for AdSense explicitly marking the ad status
    if (!adRef.current) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-ad-status') {
          const status = adRef.current.getAttribute('data-ad-status');
          if (status === 'filled') {
            setAdFilled(true);
          } else if (status === 'unfilled') {
            setAdFilled(false);
          }
        }
      });
    });

    observer.observe(adRef.current, { attributes: true });

    return () => observer.disconnect();
  }, []);

  if (!adFilled) return null;

  const shadowAvatar = getAvatarForShadow('Ad Sponsor'); // A random avatar demo

  return (
    <div className="bg-black p-4 sm:p-5 border-b border-gray-800 transition-all cursor-pointer relative">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-800 overflow-hidden flex items-center justify-center shrink-0 border border-gray-700">
             {shadowAvatar ? (
               <img src={shadowAvatar} alt="Ad" className="w-full h-full object-cover" />
             ) : (
               <User className="w-6 h-6 text-gray-500" />
             )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-white text-[15px]">
                Sponsored Partner
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-500 text-[13px]">
              <span>Promoted</span>
              <span>•</span>
              <span>Global</span>
            </div>
          </div>
        </div>

        <div className="relative">
          <span className="text-[11px] font-bold text-gray-500 uppercase tracking-widest border border-gray-700 px-2 py-0.5 rounded-sm">
            Ad
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="mb-4">
        <p className="text-gray-100 text-[15px] leading-relaxed whitespace-pre-wrap font-medium">
          Check out this amazing offer carefully selected for you.
        </p>
        <div className="mt-3 rounded-xl overflow-hidden border border-gray-800 bg-gray-900/50 flex items-center justify-center">
            <ins 
                className="adsbygoogle"
                style={{ display: 'block', width: '100%' }}
                data-ad-client="ca-pub-6445006725121373"
                data-ad-slot="1234567890" 
                data-ad-format="fluid"
                data-ad-layout-key="-fb+5w+4e-db+86"
                ref={adRef}
            ></ins>
        </div>
      </div>

      {/* Interactions */}
      <div className="flex items-center justify-between border-t border-gray-800 pt-3 mt-2">
        <button className="flex items-center gap-2 group transition-colors text-gray-400 hover:text-pink-500">
          <div className="p-2 rounded-full transition-colors group-hover:bg-pink-500/10">
            <Heart size={20} className="scale-100" />
          </div>
          <span className="text-sm font-medium">{randomStats.likes}</span>
        </button>

        <button className="flex items-center gap-2 text-gray-400 hover:text-[#0BD9E5] group transition-colors">
          <div className="p-2 rounded-full group-hover:bg-[#0BD9E5]/10 transition-colors">
            <MessageCircle size={20} />
          </div>
          <span className="text-sm font-medium">{randomStats.comments}</span>
        </button>

        <div className="flex items-center gap-2 text-gray-400">
          <div className="p-2">
            <Eye size={20} />
          </div>
          <span className="text-sm font-medium">{randomStats.views}</span>
        </div>

        <button className="flex items-center gap-2 text-gray-400 hover:text-green-500 group transition-colors">
          <div className="p-2 rounded-full group-hover:bg-green-500/10 transition-colors">
            <Share2 size={20} />
          </div>
          <span className="text-sm font-medium">{randomStats.shares > 0 ? randomStats.shares : ''}</span>
        </button>
      </div>
    </div>
  );
};
